import type { ChargeType } from './billing';
import type { IsoDate, IsoDateTime, Money } from './common';
import type { ReturnCondition } from './returns';

// Performans: the three numbers a fulfillment customer asks about, for the warehouse
// (all sellers or one) and for each seller (its own orders and products).

/** Common 3PL service levels; the cards turn amber below them, red well below. */
export const PERFORMANCE_TARGETS = {
  /** share of orders handed to the carrier by their deadline */
  onTime: 0.98,
  /** share of packed orders without a wrong product or extra unit caught at the packing table */
  pickAccuracy: 0.995,
  /** share of counted lines (product / lot in a bin) that matched the books */
  stockAccuracy: 0.98,
} as const;

export interface PerformanceQuery {
  /** First day, local time (YYYY-MM-DD) */
  readonly from: IsoDate;
  /** Last day, inclusive */
  readonly to: IsoDate;
  readonly warehouseId?: string | null;
  /** The warehouse looks at one seller; a seller always sees only itself */
  readonly tenantId?: string | null;
}

/** Zamanında kargo: orders whose deadline fell in the period and is past. */
export interface OnTimeKpi {
  readonly due: number;
  readonly onTime: number;
  /** handed to the carrier after the deadline */
  readonly late: number;
  /** still not handed over, deadline passed */
  readonly overdue: number;
  /** onTime / due; null without orders */
  readonly rate: number | null;
}

/** Toplama doğruluğu: orders packed in the period. */
export interface PickAccuracyKpi {
  readonly packed: number;
  /** a wrong product or an extra unit was scanned at the packing table */
  readonly withErrors: number;
  readonly wrongProductScans: number;
  readonly excessScans: number;
  readonly rate: number | null;
}

/** Stok doğruluğu: count tasks closed in the period (the last round of each). */
export interface StockAccuracyKpi {
  readonly bins: number;
  readonly binsMatched: number;
  readonly lines: number;
  readonly linesMatched: number;
  readonly unitsExpected: number;
  /** sum of |counted − expected| */
  readonly unitsOff: number;
  /** linesMatched / lines */
  readonly rate: number | null;
  /** 1 − unitsOff / unitsExpected */
  readonly unitRate: number | null;
}

export interface PerformanceDay {
  readonly date: IsoDate;
  readonly due: number;
  readonly onTime: number;
  readonly packed: number;
  readonly packedClean: number;
  readonly linesCounted: number;
  readonly linesMatched: number;
}

export interface PerformanceReport {
  readonly from: IsoDate;
  readonly to: IsoDate;
  readonly onTime: OnTimeKpi;
  readonly pickAccuracy: PickAccuracyKpi;
  readonly stockAccuracy: StockAccuracyKpi;
  readonly days: readonly PerformanceDay[];
  /** the Ölçüm başlangıcı behind every figure of the page (this report, its details and its activity) */
  readonly measurement: PerformanceMeasurement;
}

// --- Ölçüm başlangıcı -------------------------------------------------------------------------------
// Trial runs while a warehouse is set up (test orders, wrong scans at the packing table, count
// differences, late test orders) need not stay in the figures. A warehouse admin sets a start, for the
// whole warehouse or for one seller in it, and Performans counts only what happened from then on, each
// figure by its own moment (ordered, deadline, packed, handed over, delivered, return received, count
// closed, service date). Nothing is deleted, and billing (charges, invoices, hak ediş) does not change.

export interface PerformanceStart {
  readonly id: string;
  readonly warehouseId: string;
  /** null: the warehouse's own start, for every seller in it */
  readonly tenantId: string | null;
  readonly tenantCode: string | null;
  /** activity before it is not counted */
  readonly startsAt: IsoDateTime;
  /** when it was set, and by whom (the warehouse side only) */
  readonly setAt: IsoDateTime;
  readonly setBy: string | null;
  /**
   * The start this one replaced, which "Sıfırlamayı geri al" brings back; null: none, the figures before
   * it count again (the warehouse side only).
   */
  readonly replacedStartsAt: IsoDateTime | null;
}

/** A warehouse a seller works in (orders, stock or shipments there), on a page of no warehouse */
export interface MeasuredWarehouse {
  readonly warehouseId: string;
  readonly warehouseCode: string;
  /** the seller's figures there count from it (the later of the warehouse's start and the seller's own); null: from the beginning */
  readonly since: IsoDateTime | null;
}

export interface PerformanceMeasurement {
  /**
   * From when the figures of this view count: nothing before it is counted; null: some count from the
   * beginning. One seller in a warehouse (the warehouse looking at one): the later of the warehouse's
   * start and the seller's own. All sellers: the warehouse's start; a seller with a later start of its
   * own counts from that one in its rows. A page of no warehouse (a seller's own): the earliest of its
   * `warehouses`, null when one of them has no start.
   */
  readonly since: IsoDateTime | null;
  /** the period ends before `since`: nothing in it is counted */
  readonly periodBeforeStart: boolean;
  /** a warehouse's view: the warehouse's own start */
  readonly warehouse: PerformanceStart | null;
  /** a warehouse's view: sellers' own starts in it, every seller's for all sellers, the one seller's otherwise */
  readonly sellers: readonly PerformanceStart[];
  /**
   * A page of no warehouse (a seller's own): the warehouses the seller works in, each with the moment its
   * figures there count from. The starts of other warehouses bear on none of its figures. Empty in a
   * warehouse's view.
   */
  readonly warehouses: readonly MeasuredWarehouse[];
}

/** "Performansı sıfırla": the whole warehouse, or one seller in it (tenantId). */
export interface PerformanceStartInput {
  readonly warehouseId: string;
  readonly tenantId?: string | null;
  /** null or absent: now; never later than now */
  readonly startsAt?: IsoDateTime | null;
}

/** The later of two starts (either may be missing). */
export function laterStart(a: IsoDateTime | null, b: IsoDateTime | null): IsoDateTime | null {
  if (a === null) return b;
  if (b === null) return a;
  return Date.parse(a) >= Date.parse(b) ? a : b;
}

export const PERFORMANCE_DETAILS = ['late', 'pick_errors', 'count_differences'] as const;
export type PerformanceDetail = (typeof PERFORMANCE_DETAILS)[number];
/** The rows behind a number list the newest this many at most */
export const PERFORMANCE_DETAIL_LIMIT = 200;

export interface LateOrderRow {
  readonly orderId: string;
  readonly orderNumber: string;
  readonly tenantCode: string;
  readonly channel: string;
  readonly dueAt: IsoDateTime;
  /** null: still not handed to the carrier */
  readonly shippedAt: IsoDateTime | null;
  readonly hoursLate: number;
}

export interface PackingErrorRow {
  readonly at: IsoDateTime;
  readonly orderId: string;
  readonly orderNumber: string;
  readonly tenantCode: string;
  readonly barcode: string;
  readonly result: 'wrong_product' | 'quantity_exceeded';
  /** who packed (warehouse side only) */
  readonly packer: string | null;
}

export interface CountDifferenceRow {
  readonly closedAt: IsoDateTime;
  readonly taskNumber: string;
  /** bin code (warehouse side only) */
  readonly location: string | null;
  readonly tenantCode: string;
  readonly sku: string;
  readonly name: string;
  readonly lotNo: string | null;
  readonly expected: number;
  readonly counted: number;
  readonly difference: number;
}

export const WEEKDAY_SHORT_LABELS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'] as const;

// --- Siparişler, gönderim, iadeler, ücretler, ürünler, kanallar, bölgeler -------------------------
// Built only from what an order keeps once its buyer's personal data is erased (KVKK): numbers,
// lines, prices, dates, carrier, returns, charges, and the address's district, city and country.

/** How the chart of a period groups its days: days up to two months, weeks up to about half a year, months beyond. */
export type ActivityGrain = 'day' | 'week' | 'month';

export function activityGrain(from: IsoDate, to: IsoDate): ActivityGrain {
  const days = Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1;
  return days <= 62 ? 'day' : days <= 200 ? 'week' : 'month';
}

/** The products table lists the first ones by units shipped; a city lists its first districts. */
export const ACTIVITY_PRODUCT_LIMIT = 50;
export const ACTIVITY_DISTRICT_LIMIT = 50;

export interface ActivityOrders {
  /** ordered in the period, cancelled ones included */
  readonly received: number;
  /** of those, cancelled since */
  readonly cancelled: number;
  /** units of the received orders not cancelled (lines the marketplace cancelled left out) */
  readonly units: number;
  /** handed to the carrier in the period, whenever they were ordered */
  readonly shipped: number;
  readonly shippedUnits: number;
  /** delivered to the buyer in the period */
  readonly delivered: number;
  /**
   * order totals of the received orders not cancelled, per currency, less the lines the marketplace cancelled
   * since; an order without a total (or with a cancelled line without a price) counts its live lines' prices
   */
  readonly sales: readonly Money[];
  /** received orders not cancelled whose worth neither the total nor the lines' prices tell (left out of the sales) */
  readonly salesUnknown: number;
}

/** Gönderim hızı: the orders handed to the carrier in the period, and those delivered in it. */
export interface ActivitySpeed {
  readonly shipped: number;
  /** hours from the order to the carrier: the median and the 90th percentile */
  readonly medianHours: number | null;
  readonly p90Hours: number | null;
  /** handed over within 24 hours of the order */
  readonly within24h: number;
  readonly within24hRate: number | null;
  /** handed over after their deadline (ship_by), and by how many hours on average */
  readonly late: number;
  readonly lateAvgHours: number | null;
  readonly delivered: number;
  /** hours from the carrier to the buyer, median, of the orders delivered in the period */
  readonly deliveryMedianHours: number | null;
}

export interface ActivityReturns {
  /** orders with a return received at the returns desk in the period, and the units that came back */
  readonly orders: number;
  readonly units: number;
  /** the units by the desk's verdict on them (a fixed list; the buyer's own words never show here) */
  readonly byCondition: readonly { readonly condition: ReturnCondition; readonly units: number }[];
  /** İade oranı: of the orders handed to the carrier in the period, those with a return received so far */
  readonly shipped: number;
  readonly shippedReturned: number;
  readonly rate: number | null;
}

export interface ActivityFeeRow {
  readonly type: ChargeType;
  readonly currency: string;
  /** decimal string, VAT excluded */
  readonly amount: string;
  readonly count: number;
}

/** Hizmet ücretleri by service date: what the seller pays, what the warehouse earns. */
export interface ActivityFees {
  readonly byType: readonly ActivityFeeRow[];
  readonly totals: readonly Money[];
  /** the totals divided by the orders handed to the carrier in the period */
  readonly perShippedOrder: readonly Money[];
}

export interface ActivityProductRow {
  readonly productId: string;
  readonly tenantCode: string;
  readonly sku: string;
  readonly name: string;
  /** orders handed to the carrier in the period with this product, and its units in them */
  readonly orders: number;
  readonly units: number;
  /** units received back at the returns desk in the period */
  readonly returnedUnits: number;
}

export interface ActivityChannelRow {
  readonly channel: string;
  readonly received: number;
  readonly cancelled: number;
  /** units of the orders not cancelled */
  readonly units: number;
  /** share of all orders received in the period */
  readonly share: number;
}

/** An il or an ilçe: orders handed to the carrier in the period that went there. */
export interface ActivityPlaceRow {
  /** the comparison key (placeKey); '' for addresses without one */
  readonly key: string;
  readonly name: string;
  readonly orders: number;
  /** a city's share of all the orders, a district's of its city's */
  readonly share: number;
  /** of those, delivered by now, and the median hours from the carrier to the buyer */
  readonly delivered: number;
  readonly deliveryMedianHours: number | null;
}

export interface ActivityCityRow extends ActivityPlaceRow {
  /** most orders first, the first ACTIVITY_DISTRICT_LIMIT of districtCount */
  readonly districts: readonly ActivityPlaceRow[];
  readonly districtCount: number;
}

/** The warehouse's view of all its sellers side by side. */
export interface ActivitySellerRow {
  readonly tenantId: string;
  readonly tenantCode: string;
  readonly tenantName: string;
  readonly received: number;
  readonly units: number;
  readonly shipped: number;
  /** Zamanında kargo as on its card: orders whose deadline fell in the period */
  readonly due: number;
  readonly onTimeRate: number | null;
  /** orders with a return received in the period */
  readonly returnedOrders: number;
  readonly fees: readonly Money[];
}

export interface ActivityBucket {
  /** the days of the period the bar stands for (a week or a month cut at the period's ends) */
  readonly from: IsoDate;
  readonly to: IsoDate;
  readonly received: number;
  readonly shipped: number;
}

export interface ActivityReport {
  readonly from: IsoDate;
  readonly to: IsoDate;
  readonly orders: ActivityOrders;
  readonly speed: ActivitySpeed;
  readonly returns: ActivityReturns;
  readonly fees: ActivityFees;
  /** most units shipped first, the first ACTIVITY_PRODUCT_LIMIT of productCount */
  readonly products: readonly ActivityProductRow[];
  readonly productCount: number;
  readonly channels: readonly ActivityChannelRow[];
  /** most orders first */
  readonly cities: readonly ActivityCityRow[];
  /** the warehouse looking at all its sellers; null for one seller */
  readonly sellers: readonly ActivitySellerRow[] | null;
  readonly grain: ActivityGrain;
  readonly series: readonly ActivityBucket[];
}
